import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from "@repo/app-types/constants/shared";
import { IconUpload } from "@tabler/icons-react";
import { type ChangeEvent, type FC, useRef, useState } from "react";
import { Button, ButtonVariant } from "../Buttons/button.js";

interface FileUploadProps {
  onUpload: (file: File) => Promise<string>;
  isLoading?: boolean;
  label?: string;
  isDisabled?: boolean;
}

const IMAGE_FILE_TYPES = "image/jpeg,image/png";

export const FileUpload: FC<FileUploadProps> = ({
  onUpload,
  isLoading,
  label,
  isDisabled,
}) => {
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      if (file === undefined) return;

      if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        setErrorMessage(
          `File size exceeds the ${MAX_UPLOAD_FILE_SIZE_MB}MB limit.`,
        );
        return;
      }

      setUploading(true);
      setErrorMessage("");

      try {
        await onUpload(file);
      } catch (error) {
        setErrorMessage("Failed to upload file.");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleButtonClick = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      <Button
        icon={<IconUpload className="h-4 w-4" />}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onClick={handleButtonClick}
        variant={ButtonVariant.Secondary}
      >
        {label || "Upload"}
      </Button>
      <input
        accept={IMAGE_FILE_TYPES}
        className="sr-only"
        disabled={uploading}
        id="file-upload"
        name="file-upload"
        onChange={(e) => void handleFileChange(e)}
        ref={fileInputRef}
        type="file"
      />
      {errorMessage ? (
        <p className="text-sm text-red-500">{errorMessage}</p>
      ) : null}
    </div>
  );
};
